import React, { useEffect, useState } from 'react'
import { Toast } from 'antd-mobile'
import annualRateIcon from '@/assets/application-flow-skins/shared/products/icon-annual-rate.svg'
import flexibleLimitIcon from '@/assets/application-flow-skins/shared/products/icon-flexible-limit.svg'
import flexibleRepaymentIcon from '@/assets/application-flow-skins/shared/products/icon-flexible-repayment.svg'
import shieldRedIcon from '@/assets/application-flow-skins/shared/products/icon-shield-red.png'
import platformIcon from '@/assets/application-flow-skins/shared/products/platform-icon.png'
import {
  ApplicationFlowHeader,
  ApplicationFlowProductRetentionDialog,
  ApplicationFlowSubmitBlock,
} from '../components'
import { AuthorizeSkinProps } from '../types'
import themeStyles from '../theme/application-flow-theme.module.scss'
import { AuthorizeSkinController } from './hooks/use-authorize-skin-controller'
import styles from './authorize-skin.module.scss'

interface Props extends AuthorizeSkinProps {

  /** 只负责皮肤内部协议确认弹窗与挽留弹窗的本地交互控制器。 */
  controller: AuthorizeSkinController
}

const LIMIT_LABELS: Record<string, string> = {
  1: '30,000',
  2: '50,000',
  3: '100,000',
  4: '200,000',
}

const formatAmount = (value: string | number | undefined): string => (
  LIMIT_LABELS[String(value || '')] || '100,000'
)

/**
 * AuthorizeSkin — 授权阶段的纯展示皮肤。
 * @returns {React.ReactElement} 授权展示节点；产品、协议和提交动作均通过 Props 委托旧容器
 */
const AuthorizeSkin: React.FC<Props> = React.memo(({
  controller,
  model,
  onOpenAgreement,
  onToggleAgreement,
  onToggleProduct,
  theme,
}) => {
  const [activeProtocolKey, setActiveProtocolKey] = useState<string>('')
  const amount = formatAmount(model.applyLimit)
  const isProductSelectionEnabled = model.productSelectionEnabled

  useEffect(() => {
    if (!model.submitting) {
      return undefined
    }
    Toast.show({
      icon: 'loading',
      content: '领取中…',
      duration: 0,
    })
    return () => Toast.clear()
  }, [model.submitting])

  // 只有已由旧容器取回正文的协议才能在当前页预览，避免静态协议名称套用另一份后端正文。
  const previewAgreements = model.agreements.filter(agreement => Boolean(agreement.content))
  const activeAgreementKey = activeProtocolKey || previewAgreements[0]?.key || ''
  const activeAgreement = previewAgreements.find(agreement => agreement.key === activeAgreementKey) || previewAgreements[0]
  let { submitLabel } = controller

  if (controller.productCountdownText) {
    submitLabel = '立即领取'
  }
  if (model.submitting) {
    submitLabel = '提交中'
  }

  return (
    <main className={themeStyles.themeRoot} data-theme={theme.name}>
      <section className={styles.page}>
        <ApplicationFlowHeader onBack={controller.onBack} title="匹配结果" visualType="solid" />
        <div className={styles.content}>
          <section className={styles.hero}>
            <img alt="" aria-hidden="true" className={styles.heroIllustration} height="59" src={theme.productsIllustration} width="50" />
            <div>
              <h1>已为您匹配到以下资方或助贷机构</h1>
              <p>审核通过后请尽快完成提现</p>
            </div>
          </section>
          <section className={styles.amountCard}>
            <span>最高可借额度(元)</span>
            <strong>{amount}</strong>
            <div className={styles.features}>
              <span>
                <img alt="" className={styles.featureIcon} height="14" src={annualRateIcon} width="14" />
                年化利率7.2%-24%
              </span>
              <span>
                <img alt="" className={styles.featureIcon} height="14" src={flexibleRepaymentIcon} width="14" />
                随借随还
              </span>
              <span>
                <img alt="" className={styles.featureIcon} height="14" src={flexibleLimitIcon} width="14" />
                额度灵活
              </span>
            </div>
          </section>
          <section className={styles.productsCard}>
            {model.products.map(product => {
              const checked = model.selectedProductIds.includes(product.id)
              const canToggle = isProductSelectionEnabled
              const productClass = `${styles.product} ${product.platformName ? styles.hasPlatformBanner : ''}`
              let selectionControl: React.ReactNode = null
              if (checked) {
                selectionControl = <img alt="已选中" className={styles.selected} height="26" src={theme.productSelectedIcon} width="26" />
              } else if (canToggle) {
                selectionControl = <span aria-hidden="true" className={styles.unselected} />
              }

              return (
                <button
                  aria-pressed={checked}
                  className={productClass}
                  key={product.id}
                  onClick={() => canToggle && onToggleProduct(product.id)}
                  type="button"
                >
                  {product.platformName ? (
                    <span className={styles.platformBanner} aria-label={`服务平台：${product.platformName}`}>
                      <img alt="" aria-hidden="true" className={styles.platformIcon} height="16" src={platformIcon} width="16" />
                      <span>服务平台：{product.platformName}</span>
                    </span>
                  ) : null}
                  <span className={styles.productInfo}>
                    <strong>{product.name || '借款产品'}</strong>
                    {product.companyName ? <small>由{product.companyName}为您服务</small> : null}
                  </span>
                  {selectionControl}
                </button>
              )
            })}
          </section>
          <section className={styles.notice}>
            <h2>
              <img alt="" aria-hidden="true" className={styles.noticeShield} height="18" src={shieldRedIcon} width="14" />
              谨防电信诈骗
            </h2>
            <p>请勿接听任何境外电话！任何放款前以缴纳保证金、中介费等名义的行为均为诈骗，任何要求您提供银行卡密码、验证码等个人信息均为诈骗。</p>
          </section>
          {activeAgreement ? (
            <section className={styles.protocols}>
              {previewAgreements.length > 1 ? (
                <div className={styles.protocolTabs} role="tablist">
                  {previewAgreements.map(agreement => (
                    <button
                      aria-selected={activeAgreementKey === agreement.key}
                      key={agreement.key}
                      onClick={() => setActiveProtocolKey(agreement.key)}
                      role="tab"
                      type="button"
                    >
                      {agreement.name}
                    </button>
                  ))}
                </div>
              ) : null}
              <article className={styles.protocolContent}>
                <button
                  className={styles.protocolTitle}
                  onClick={() => onOpenAgreement(activeAgreement.key)}
                  type="button"
                >
                  {activeAgreement.name || model.agreementTitle || '协议内容'}
                </button>
                <div
                  className={styles.protocolText}
                  dangerouslySetInnerHTML={{ __html: activeAgreement.content || '' }}
                />
              </article>
            </section>
          ) : null}
        </div>
        <ApplicationFlowSubmitBlock
          agreementChecked={model.agreementChecked}
          agreements={model.agreements}
          countdownText={controller.productCountdownText}
          disabled={model.submitDisabled || model.submitting}
          onOpenAgreement={onOpenAgreement}
          onSubmit={controller.onSubmit}
          onToggleAgreement={onToggleAgreement}
          submitBadge={controller.productCountdownText ? {
            text: '额度即将失效',
            tone: ['orange', 'gold'].includes(theme.name) ? 'dark' : 'default',
          } : undefined}
          submitLabel={submitLabel}
        />
        {controller.agreementDialogVisible ? (
          <div aria-label="协议确认" aria-modal="true" className={styles.dialogMask} role="dialog">
            <section className={styles.dialogCard}>
              <h2>请阅读并同意协议</h2>
              <p>
                您点击“同意”，即表示您已阅读并同意
                {model.agreements.map((agreement, index) => (
                  <React.Fragment key={agreement.key}>
                    {index > 0 ? '、' : ''}
                    <button onClick={() => onOpenAgreement(agreement.key)} type="button">《{agreement.name}》</button>
                  </React.Fragment>
                ))}
                的全部条款
              </p>
              <div className={styles.dialogActions}>
                <button className={styles.dialogSecondary} onClick={controller.onCloseAgreementDialog} type="button">不同意</button>
                <button className={styles.dialogPrimary} onClick={controller.onConfirmAgreement} type="button">同意</button>
              </div>
            </section>
          </div>
        ) : null}
        <ApplicationFlowProductRetentionDialog
          agreements={model.agreements}
          amount={amount}
          brandName={model.brandName}
          canClose={controller.retentionCanClose}
          countdownText={controller.productCountdownText}
          illustrationSrc={theme.productsRetentionIllustration}
          onDismiss={controller.onCloseRetention}
          onOpenAgreement={onOpenAgreement}
          onSubmit={controller.onSubmitFromRetention}
          submitting={model.submitting}
          visible={controller.retentionVisible}
        />
      </section>
    </main>
  )
})

AuthorizeSkin.displayName = 'AuthorizeSkin'

export default AuthorizeSkin
